// =================================================================================================
// C# ASP.NET CORE REVERSE PROXY GATEKEEPER INTEGRATION DOCUMENTATION
// =================================================================================================
// A. Reverse Proxy Middleware (Server-Side Security Gate)
// File: Server/Middleware/SecurityGateMiddleware.cs
// Checks for X-2FA-Verified cookie. If present, proxies to http://localhost:8181.
// If missing, redirects to /login.
//
// B. Admin User Management
// File: Server/Controllers/FirebaseAdminController.cs
// Secured by X-Admin-Key header. Allows disabling/deleting users.
// =================================================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { 
    getAuth, 
    signInWithEmailAndPassword, 
    signOut, 
    onAuthStateChanged, 
    updatePassword, 
    EmailAuthProvider, 
    reauthenticateWithCredential,
    sendPasswordResetEmail // RESTORED
} from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { Shield, Lock, CheckCircle, RotateCw, Key, LogOut, Settings, AlertTriangle, User, ExternalLink, QrCode, RefreshCw } from 'lucide-react';

// PRODUCTION IMPORTS
import * as OTPAuth from 'otpauth';
import { QRCodeSVG } from 'qrcode.react';

// LOGO IMPORT
// Ensure you have saved your logo at ClientApp/src/assets/logo.png
// If the file is missing, the build will fail. 
// To use a placeholder temporarily, uncomment the line below instead:
// const companyLogo = "https://via.placeholder.com/150x50?text=GENESIS+LOGO";
import companyLogo from './assets/logo.png'; 

// =================================================================
// 1. CONFIGURATION & CONSTANTS
// =================================================================

const LOCAL_FIREBASE_CONFIG = {
    apiKey: "AIzaSyCURf-oOizhv1qOLKgtjCuizOk5UoUXsXI",
    authDomain: "genesisgatekeeper.firebaseapp.com",
    projectId: "genesisgatekeeper",
    storageBucket: "genesisgatekeeper.firebasestorage.app",
    messagingSenderId: "985688670590",
    appId: "1:985688670590:web:c1e69cf209036544b7332f",
    measurementId: "G-2WRL5E61XH"
};

const SYSTEM_CONFIG = {
    policyId: 'admin_genesis_com_policy',
    passwordExpiryDays: 90
};

const FLOW_STEPS = {
    LOGIN: 'LOGIN',
    CHANGE_PASSWORD: 'CHANGE_PASSWORD', 
    POLICY_CHECK: 'POLICY_CHECK',
    TOTP_SETUP: 'TOTP_SETUP',
    TOTP_CHALLENGE: 'TOTP_CHALLENGE',
    ACCESS_GRANTED: 'ACCESS_GRANTED',
    ERROR: 'ERROR',
    INITIALIZING: 'INITIALIZING',
};

const app = initializeApp(LOCAL_FIREBASE_CONFIG);
const auth = getAuth(app);
const db = getFirestore(app);
const appId = LOCAL_FIREBASE_CONFIG.projectId;

const getPolicyPath = (uid) => 
    `/artifacts/${appId}/users/${uid}/user_2fa_policies/${SYSTEM_CONFIG.policyId}`;

// =================================================================
// 2. MAIN APP COMPONENT
// =================================================================

const Gatekeeper = () => {
    const [flowStep, setFlowStep] = useState(FLOW_STEPS.LOGIN);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);
    const [userId, setUserId] = useState(null);
    
    // Inputs
    const [emailInput, setEmailInput] = useState('');
    const [passwordInput, setPasswordInput] = useState('');

    // Password Reset Inputs
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    
    // Opt-out State for First Time Login
    const [optInMfa, setOptInMfa] = useState(true); 

    const [policy, setPolicy] = useState({ is2faEnabled: false, secret: null, passwordLastChanged: null });
    const [totpInput, setTotpInput] = useState('');
    const [generatedSecret, setGeneratedSecret] = useState(null);

    // -------------------------
    // 2.1 HELPERS
    // -------------------------

    const generateNewSecret = () => {
        const newSecret = new OTPAuth.Secret({ size: 20 });
        setGeneratedSecret(newSecret.base32);
        return newSecret.base32;
    };

    const validateTotp = (token, secretBase32) => {
        if (!secretBase32) return false;
        
        const totp = new OTPAuth.TOTP({
            issuer: "Genesis Gatekeeper",
            label: emailInput || "User",
            algorithm: "SHA1",
            digits: 6,
            period: 30,
            secret: OTPAuth.Secret.fromBase32(secretBase32)
        });

        // Validate returns the delta (index) if valid, or null if invalid
        // window: 1 allows for slight clock drift (+/- 30s)
        const delta = totp.validate({ token: token, window: 1 });
        return delta !== null;
    };

    const isPasswordExpired = (timestamp) => {
        if (!timestamp) return true; // Treat missing timestamp as expired (Force first login change)
        const lastChanged = timestamp.toDate ? timestamp.toDate() : new Date(timestamp); 
        const now = new Date();
        const diffTime = Math.abs(now - lastChanged);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
        return diffDays > SYSTEM_CONFIG.passwordExpiryDays;
    };

    // -------------------------
    // 2.2 HANDLERS
    // -------------------------

    const handleSimulateLogout = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        setSuccessMsg(null);
        try {
            await signOut(auth);
        } catch (e) {
            console.error("Logout error:", e);
        }
        setUserId(null);
        setFlowStep(FLOW_STEPS.LOGIN);
        setTotpInput('');
        setNewPassword('');
        setConfirmPassword('');
        setOldPassword('');
        setIsLoading(false);
    }, []);

    const handlePrimaryLogin = useCallback(async (e) => {
        if (e) e.preventDefault();
        setIsLoading(true);
        setError(null);
        setSuccessMsg(null);

        try {
            const userCredential = await signInWithEmailAndPassword(auth, emailInput, passwordInput);
            const currentUid = userCredential.user.uid;
            setUserId(currentUid);
            
            // Fetch Policy & Password Status
            const policyRef = doc(db, getPolicyPath(currentUid));
            const docSnap = await getDoc(policyRef);
            
            let currentData = { is2faEnabled: false, secret: null, passwordLastChanged: null };

            if (docSnap.exists()) {
                currentData = docSnap.data();
            } else {
                // Init default policy for new user
                const initialPolicy = { 
                    is2faEnabled: true, 
                    secret: null, 
                    passwordLastChanged: null, 
                    createdAt: new Date() 
                };
                await setDoc(policyRef, initialPolicy);
                currentData = initialPolicy;
            }
            setPolicy(currentData);

            // *** PASSWORD EXPIRY CHECK ***
            if (isPasswordExpired(currentData.passwordLastChanged)) {
                setOldPassword(passwordInput); 
                setOptInMfa(true); // Default to checked
                setFlowStep(FLOW_STEPS.CHANGE_PASSWORD);
                setIsLoading(false);
                return; 
            }

            // Normal Flow Logic
            if (currentData.is2faEnabled) {
                if (currentData.secret) {
                    setFlowStep(FLOW_STEPS.TOTP_CHALLENGE);
                } else {
                    generateNewSecret();
                    setFlowStep(FLOW_STEPS.TOTP_SETUP);
                }
            } else {
                setFlowStep(FLOW_STEPS.ACCESS_GRANTED);
            }

        } catch (e) {
            console.error("Authentication Error:", e.code, e.message);
            setError("Authentication failed. Invalid email or password.");
            setFlowStep(FLOW_STEPS.LOGIN);
        } finally {
            setIsLoading(false);
        }
    }, [db, emailInput, passwordInput]);

    // Handle Forgot Password Request
    const handleForgotPassword = async (e) => {
        if (e) e.preventDefault();
        if (!emailInput) {
            setError("Please enter your email address first.");
            return;
        }
        
        setIsLoading(true);
        setError(null);
        setSuccessMsg(null);

        try {
            await sendPasswordResetEmail(auth, emailInput);
            setSuccessMsg(`Reset link sent to ${emailInput}. Check your inbox/spam.`);
        } catch (e) {
            console.error("Reset Email Error:", e);
            if (e.code === 'auth/user-not-found') {
                setError("If that email exists, a link has been sent."); 
            } else {
                setError("Failed to send reset email. Try again later.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordChangeSubmit = useCallback(async (e) => {
        if (e) e.preventDefault();
        setError(null);

        if (newPassword !== confirmPassword) {
            setError("New passwords do not match.");
            return;
        }
        if (newPassword.length < 6) {
            setError("Password must be at least 6 characters.");
            return;
        }

        setIsLoading(true);

        try {
            const user = auth.currentUser;
            // Re-authenticate to ensure session is fresh/valid for password change
            const credential = EmailAuthProvider.credential(user.email, oldPassword);
            await reauthenticateWithCredential(user, credential);

            // Update Password in Auth
            await updatePassword(user, newPassword);

            // Update Firestore timestamp
            const policyRef = doc(db, getPolicyPath(user.uid));
            
            // UPDATE: Save password change time AND the Opt-In choice
            await setDoc(policyRef, { 
                passwordLastChanged: new Date(),
                is2faEnabled: optInMfa // Set based on checkbox
            }, { merge: true });

            // Update local password input to match new password
            setPasswordInput(newPassword);

            // Reset Password Fields
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');

            // Refetch to decide next step
            const docSnap = await getDoc(policyRef);
            const currentData = docSnap.data();
            
            // If user opted OUT (false), this block skips.
            // If user opted IN (true), we go to setup.
            if (currentData.is2faEnabled) {
                if (currentData.secret) {
                    setFlowStep(FLOW_STEPS.TOTP_CHALLENGE);
                } else {
                    generateNewSecret();
                    setFlowStep(FLOW_STEPS.TOTP_SETUP);
                }
            } else {
                setFlowStep(FLOW_STEPS.ACCESS_GRANTED);
            }

        } catch (e) {
            console.error("Password Change Error:", e);
            if (e.code === 'auth/wrong-password') {
                setError("The old password entered is incorrect.");
            } else {
                setError("Failed to change password. Try again.");
            }
        } finally {
            setIsLoading(false);
        }
    }, [newPassword, confirmPassword, oldPassword, db, optInMfa]);

    const handleTotpSubmit = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        const isValid = validateTotp(totpInput, policy.secret);

        if (isValid) {
            setFlowStep(FLOW_STEPS.ACCESS_GRANTED);
        } else {
            setError('Invalid Code. Please check your phone time or try again.');
            setTotpInput('');
        }
        setIsLoading(false);
    }, [totpInput, policy.secret, emailInput]);

    const handleSetupConfirm = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        const isValid = validateTotp(totpInput, generatedSecret);

        if (isValid) {
            try {
                const policyRef = doc(db, getPolicyPath(userId));
                await setDoc(policyRef, { secret: generatedSecret }, { merge: true });
                setPolicy(prev => ({ ...prev, secret: generatedSecret }));
                setFlowStep(FLOW_STEPS.ACCESS_GRANTED);
            } catch (e) {
                setError("Failed to save 2FA configuration.");
            }
        } else {
            setError('Invalid Code. Scan the QR code and try again.');
        }
        setIsLoading(false);
    }, [totpInput, generatedSecret, userId, emailInput]);

    const handlePolicyUpdate = useCallback(async (newStatus) => {
        if (!userId) return;
        setIsLoading(true);
        try {
            const policyRef = doc(db, getPolicyPath(userId));
            await setDoc(policyRef, { is2faEnabled: newStatus }, { merge: true });
        } catch (e) {
            setError('Failed to update 2FA policy.');
        } finally {
            setIsLoading(false);
        }
    }, [userId]);

    const finalizeSessionAndRedirect = useCallback(async () => {
        if (!userId) return;
        setError(null);
        try {
            const response = await fetch(`/api/Session/grant-access?uid=${userId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            if (!response.ok) throw new Error('Server failed to set secure cookie.');
            window.location.href = '/'; 
        } catch (e) {
            console.error("Session Finalization Error:", e);
            setError('System Error: Cannot finalize session.');
            setIsLoading(false);
        }
    }, [userId]);


    // -------------------------
    // 2.3 EFFECTS
    // -------------------------

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) setUserId(user.uid);
            else {
                setUserId(null);
                if (flowStep !== FLOW_STEPS.ACCESS_GRANTED) setFlowStep(FLOW_STEPS.LOGIN);
            }
        });
        return unsubscribe;
    }, [flowStep]);

    useEffect(() => {
        if (!userId) return;
        const policyRef = doc(db, getPolicyPath(userId));
        const unsubscribe = onSnapshot(policyRef, (docSnap) => {
            if (docSnap.exists()) {
                setPolicy(docSnap.data());
            }
        });
        return unsubscribe;
    }, [userId]);

    useEffect(() => {
        if (flowStep === FLOW_STEPS.ACCESS_GRANTED) {
            const timer = setTimeout(finalizeSessionAndRedirect, 1500);
            return () => clearTimeout(timer);
        }
    }, [flowStep, finalizeSessionAndRedirect]);


    // -------------------------
    // 2.4 RENDERERS
    // -------------------------
    
    const renderCardContent = () => {
        const baseClass = "flex flex-col items-center justify-center p-8 space-y-4";

        if (flowStep === FLOW_STEPS.LOGIN) {
            return (
                <form className={baseClass} onSubmit={handlePrimaryLogin}>
                    <div className="mb-4 flex flex-col items-center">
                        <img 
                            src={companyLogo} 
                            alt="Company Logo" 
                            className="h-16 w-auto mb-2 object-contain" 
                            onError={(e) => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/150x50?text=LOGO"; }} 
                        />
                        <h2 className="text-xl font-semibold text-gray-800">Genesis Gatekeeper</h2>
                        <p className="text-sm text-gray-500">Authorized personnel only.</p>
                    </div>
                    
                    {error && <div className="text-red-600 font-medium text-xs p-2 bg-red-50 rounded-md w-full text-center">{error}</div>}
                    {successMsg && <div className="text-green-600 font-medium text-xs p-2 bg-green-50 rounded-md w-full text-center">{successMsg}</div>}
                    
                    <div className="w-full space-y-4 pt-2">
                        <input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="Email Address" required className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 text-sm outline-none" />
                        <input type="password" value={passwordInput} onChange={(e) => setPasswordInput(e.target.value)} placeholder="Password" required className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 text-sm outline-none" />
                    </div>
                    
                    <button type="submit" disabled={isLoading} className="w-full flex items-center justify-center px-4 py-3 mt-4 bg-teal-600 text-white font-medium rounded-lg shadow-md hover:bg-teal-700 disabled:bg-teal-400 transition-colors">
                        {isLoading ? <RotateCw className="w-5 h-5 animate-spin" /> : 'Continue'}
                    </button>

                    <div className="text-center mt-2">
                        <button 
                            type="button" 
                            onClick={handleForgotPassword} 
                            disabled={isLoading}
                            className="text-xs text-teal-600 hover:text-teal-800 hover:underline"
                        >
                            Forgot Password?
                        </button>
                    </div>
                    <div className="mt-4 w-full text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="font-semibold text-gray-700">System Notice</p>
                      <p className="mt-1">
                        <span className="font-medium">Genesis Gatekeeper</span> and 
                        <span className="font-medium"> Genesis Portal</span> are separate systems. 
                        Use this login page only for Genesis Gatekeeper. Do not use these 
                        credentials or this form to access the Genesis Portal or other Genesis systems.
                      </p>
                    </div>
                </form>
            );
        }

        if (flowStep === FLOW_STEPS.CHANGE_PASSWORD) {
            return (
                <form className={baseClass} onSubmit={handlePasswordChangeSubmit}>
                    <RefreshCw className="w-8 h-8 text-orange-600 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-800">Password Expired</h2>
                    <p className="text-xs text-center text-gray-500">Your password has expired or must be changed.</p>
                    
                    {error && <div className="text-red-600 font-medium text-xs p-2 bg-red-50 rounded-md w-full text-center">{error}</div>}
                    
                    <div className="w-full space-y-3 pt-2">
                        <input type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} placeholder="Old Password" required className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-orange-500 text-sm outline-none" />
                        <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New Password" required className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-orange-500 text-sm outline-none" />
                        <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm New Password" required className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-orange-500 text-sm outline-none" />
                    </div>

                    <div className="w-full pt-2">
                         <label className="flex items-start space-x-3 cursor-pointer">
                            <input 
                                type="checkbox" 
                                checked={optInMfa} 
                                onChange={(e) => setOptInMfa(e.target.checked)} 
                                className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mt-0.5"
                            />
                            <div className="text-sm text-gray-700">
                                <span className="font-medium text-gray-900">Enable 2FA (Recommended)</span>
                                <p className="text-xs text-gray-500 mt-0.5">Secure your account with an authenticator app.</p>
                            </div>
                        </label>
                    </div>
                    
                    <button type="submit" disabled={isLoading} className="w-full flex items-center justify-center px-4 py-3 mt-4 bg-orange-600 text-white font-medium rounded-lg shadow-md hover:bg-orange-700 disabled:bg-orange-400 transition-colors">
                        {isLoading ? <RotateCw className="w-5 h-5 animate-spin" /> : 'Update Password & Continue'}
                    </button>
                </form>
            );
        }

        if (flowStep === FLOW_STEPS.TOTP_SETUP) {
            let otpUri = "";
            if (generatedSecret) {
                const totp = new OTPAuth.TOTP({
                    issuer: "Genesis Gatekeeper",
                    label: emailInput || "User",
                    algorithm: "SHA1",
                    digits: 6,
                    period: 30,
                    secret: OTPAuth.Secret.fromBase32(generatedSecret)
                });
                otpUri = totp.toString();
            }

            return (
                <div className={baseClass}>
                    <QrCode className="w-8 h-8 text-purple-600 mb-2" />
                    <h2 className="text-xl font-semibold text-gray-800">Setup 2FA</h2>
                    <p className="text-xs text-center text-gray-500 px-4">Scan this QR code with your authenticator app.</p>
                    
                    <div className="p-3 bg-white border border-gray-200 rounded-lg shadow-inner">
                        {generatedSecret && <QRCodeSVG value={otpUri} size={160} />}
                    </div>

                    {error && <div className="text-red-600 font-medium text-xs p-2 bg-red-50 rounded-md w-full text-center">{error}</div>}

                    <div className="w-full space-y-2">
                        <input type="text" placeholder="000000" value={totpInput} onChange={(e) => setTotpInput(e.target.value)} maxLength={6} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center tracking-widest text-lg font-mono focus:ring-purple-500 focus:border-purple-500" />
                    </div>
                    <button onClick={handleSetupConfirm} disabled={isLoading || totpInput.length !== 6} className="w-full px-4 py-3 mt-2 bg-purple-600 text-white font-medium rounded-lg shadow-md hover:bg-purple-700 disabled:bg-purple-400">
                        {isLoading ? <RotateCw className="w-5 h-5 animate-spin mx-auto" /> : 'Verify & Activate'}
                    </button>
                </div>
            );
        }

        if (flowStep === FLOW_STEPS.TOTP_CHALLENGE) {
            return (
                <div className={baseClass}>
                    <Key className="w-8 h-8 text-orange-500 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-800">Verification</h2>
                    <p className="text-sm text-center text-gray-500">Enter the 6-digit code from your app.</p>
                    {error && <div className="text-red-600 font-medium text-xs p-2 bg-red-50 rounded-md w-full text-center">{error}</div>}
                    <div className="w-full space-y-4 pt-2">
                        <input type="text" placeholder="000000" value={totpInput} onChange={(e) => setTotpInput(e.target.value)} maxLength={6} className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center tracking-widest text-lg font-mono focus:ring-orange-500 focus:border-orange-500" />
                    </div>
                    <button onClick={handleTotpSubmit} disabled={isLoading || totpInput.length !== 6} className="w-full px-4 py-3 mt-4 bg-orange-600 text-white font-medium rounded-lg shadow-md hover:bg-orange-700 disabled:bg-orange-400">
                        {isLoading ? <RotateCw className="w-5 h-5 animate-spin mx-auto" /> : 'Verify Access'}
                    </button>
                    <button onClick={() => { handleSimulateLogout(); }} className="text-xs text-gray-400 hover:text-gray-600 underline">Cancel</button>
                </div>
            );
        }

        if (flowStep === FLOW_STEPS.ACCESS_GRANTED || flowStep === FLOW_STEPS.ERROR) {
            const isGranted = flowStep === FLOW_STEPS.ACCESS_GRANTED;
            return (
                <div className={`${baseClass} transition-colors duration-500`}>
                    <div className="text-center space-y-3">
                        {isGranted ? <CheckCircle className="w-10 h-10 text-green-500 mx-auto" /> : <AlertTriangle className="w-10 h-10 text-red-500 mx-auto" />}
                        <h2 className={`text-2xl font-bold ${isGranted ? 'text-green-600' : 'text-red-600'}`}>{isGranted ? 'ACCESS GRANTED' : 'ERROR'}</h2>
                        <p className="text-sm text-gray-600">{isGranted ? 'Session established.' : error}</p>
                    </div>
                    {isGranted && (
                        <div className="w-full text-center p-4 bg-green-50 rounded-lg border border-green-200">
                            <p className="text-xs font-medium text-green-700">Redirecting to application...</p>
                            <a href="/" className="inline-flex items-center justify-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors mt-4">
                                <ExternalLink className="w-4 h-4 mr-2" /> Proceed Manually
                            </a>
                        </div>
                    )}
                    <button onClick={handleSimulateLogout} className="w-full px-4 py-3 mt-4 bg-gray-500 text-white font-medium rounded-lg shadow-md hover:bg-gray-600">
                        <LogOut className="w-5 h-5 mx-auto" />
                    </button>
                </div>
            );
        }

        return (
            <div className={baseClass}>
                <RotateCw className="w-10 h-10 text-teal-500 animate-spin" />
                <h2 className="text-xl font-semibold text-gray-800">Loading...</h2>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 font-[Inter]">
            <header className="flex flex-col items-center mb-6">
                {/* LOGO SECTION REPLACED HERE */}
            </header>
            <div className="w-full max-w-sm bg-white rounded-xl shadow-2xl overflow-hidden ring-1 ring-gray-100">
                {renderCardContent()}
            </div>
            <div className="w-full max-w-sm mt-6 p-4 bg-white rounded-xl shadow-lg ring-1 ring-gray-100 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <Settings className="w-5 h-5 text-gray-400" />
                    <span className="text-sm font-semibold text-gray-800">2FA Policy:</span>
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${policy.is2faEnabled ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>{policy.is2faEnabled ? 'ENFORCED' : 'BYPASSED'}</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" checked={!!policy.is2faEnabled} onChange={(e) => handlePolicyUpdate(e.target.checked)} disabled={!userId || flowStep === FLOW_STEPS.ACCESS_GRANTED} />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-teal-600"></div>
                </label>
            </div>
            <footer className="mt-6 text-center text-xs text-gray-500 space-y-1 p-2">
                <p>App ID: {appId}</p>
                <p>User ID: {userId || 'N/A'}</p>
            </footer>
        </div>
    );
};

export default Gatekeeper;